import { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import Legend from "./components/Legend";
import "leaflet/dist/leaflet.css";
import "./App.css";
import RasterLayer from "./components/RasterLayer";
import { SoilCategories } from "./components/SoilCategories";
import { SoilRasters } from "./components/SoilRasters";
import { SOIL_KEYS, type CanonicalKey } from "./components/SoilKeys";
import type { Control } from "leaflet";
import { SoilRanges } from "./components/SoilRanges";
import { useMap } from "react-leaflet";
import { SoilThresholds } from "./components/SoilThresholds";
import { LandCoverGeoJSON } from "./components/LandCoverFiles";
import computeFCS from "./components/FoodConsumptionScore";
import DataDownload from "./components/DataDownload";

import type {
  Feature,
  Geometry,
  GeoJsonProperties,
  FeatureCollection,
} from "geojson";
import L, { LatLngExpression, PathOptions } from "leaflet";
import {
  BarChart,
  PieChart,
  Pie,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface GeoRaster {
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
  width: number;
  height: number;
  values: number[][][]; // assuming [band][y][x]
}

function App() {
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>(""); // e.g. "pH"
  const [geoData, setGeoData] = useState<FeatureCollection<
    Geometry,
    GeoJsonProperties
  > | null>(null);
  const [chartProperty, setChartProperty] = useState<string>("land_cover");
  const [hoveredBarKey, setHoveredBarKey] = useState<string | null>(null);
  const [wetlandSubType, setWetlandSubType] = useState<string>("");
  const [forestSubType, setForestSubType] = useState<string>("");
  const [LandCoverSubType, setLandCoverSubType] = useState<string>("");
  const [soilSubType, setSoilSubType] = useState<CanonicalKey | "">("");
  const [selectedRaster, setSelectedRaster] = useState<string>("");
  const [featureCount, setFeatureCount] = useState<number>(0);
  const [districts, setDistricts] = useState<string[]>([]);
  const [selectedTeams, setTeams] = useState<string | null>(null);
  const [isCat, setIsCat] = useState<boolean>(true);
  const [selectedSubcatchment, setSelectedSubcatchment] = useState<string>("");
  const [soilStats, setSoilStats] = useState<{
    min: number;
    max: number;
    mean: number;
    std: number;
  } | null>(null);
  const rasterRef = useRef<any>(null);
  const rasterLoadedRef = useRef<GeoRaster | null>(null);
  const legendRef = useRef<Control | null>(null);
  const [rasterBounds, setRasterBounds] = useState<L.LatLngBounds | null>(null);
  const isDegraded =
    selectedFile === "Landcover" &&
    (LandCoverSubType === "degraded_landscapes" ||
      LandCoverSubType === "degraded landscapes");
  const [histogramData, setHistogramData] = useState<
    { count: number; label: string; min: number; max: number; color: string }[]
  >([]);
  const [selectedLULCSubcatchment, setSelectedLULCSubcatchment] = useState("");
  const [subcatchments, setSubcatchments] = useState<string[]>([]);
  const [showSubcatchments, setShowSubcatchments] = useState<boolean>(true);
  const [landscapesData, setLandscapesData] =
    useState<FeatureCollection | null>(null);
  const [showLandscapes, setShowLandscapes] = useState<boolean>(true);
  const [selectedYear, setSelectedYear] = useState<string>("2025");

  const ranges = SoilRanges?.[soilSubType as CanonicalKey]; // may be undefined if ""

  const layerOptions = [
    {
      label: "Landcover",
      value: "Landcover",
      file: "Landcover",
      team: "Land cover team",
    },
    {
      label: "Vegetation",
      value: "Vegetation",
      file: "Vegetation",
      team: "Vegetation team",
      years: {
        // 👈 add this
        "2025": "/geojson/vegetation_cover_2025.geojson",
        //"2026": "/geojson/vegetation_cover_2026.geojson",
      },
      chartFields: [
        "district",
        "council_name",
        "site_name",
        "catchment",
        "observer_name",
        "observer_date",
        "landform_description",
        "vegetation_structure",
        "land_ownership",
        "rangeland_nearest_perennial_species",
        "tree_shrub",
      ],
    },

    {
      label: "Forest",
      file: "Forest",
      value: "Forest",
      isPolygon: true,
      team: "Forest team",
    },

    {
      label: "Wetland",
      value: "Wetland",
      file: "Wetland",
      team: "Wetland team",
    },

    { label: "Soil", value: "Soil", team: "Soil team" }, // 👈 Add soil here
    {
      label: "Socio-economic",
      file: "Socio-economic",
      value: "Socio-economic",
      team: "Socio-economic team",
      years: {
        "2025": "/geojson/socio-economic-data_2025.geojson",
      },
      chartFields: [
        "Number of days consumed staples",

        "Number of days consumed pulses",
        "Number of days consumed dairy",
        "Number of days consumed meat",
        "Number of days consumed eggs",

        "Number of days consumed fish",
        "Number of days consumed leafy vegetables",
        "Number of days consumed vitamin A vegetables",
        "Number of days consumed vitamin A fruits",
        "Number of days consumed sugar/sweets",
        "Number of days consumed oils/fats",
        //"food_consumption_score",
      ],
    },
  ];

  const land_cover_categories = [
    {
      label: "Landcover",
      value: "landcover",
      file: "landcover",
      team: "Land cover team",
      years: {
        2025: "/geojson/lulc_2025.geojson",
        //2026: "/geojson/lulc_2026.geojson",
        // add more as needed
      },
      chartFields: [
        "district",
        "subcatchment_code",
        "enumerator_name",
        "degradation_status",
        "conservation_practices",
        "land_cover",
      ],
    },
    {
      label: "Degraded Landscapes",
      value: "degraded_landscapes",
      file: "degraded landscapes",
      team: "Land cover team",
      isPolygon: true,
      useSubcatchments: true,
      chartFields: ["land_cover"],
    },
  ];
  const wetlandCategories = [
    {
      label: "Wetland Flora",
      file: "wetland_flora",
      years: { 2025: "/geojson/wetland_flora_2025.geojson" },
      chartFields: [
        "district",
        "herbaceousCover",
        "shrubCover",
        "bareSoil",
        "litter",
      ],
    },
    {
      label: "Wetland Fauna",
      file: "wetland_fauna",
      years: { 2025: "/geojson/wetland_fauna_2025.geojson" },
      chartFields: [
        "Avifauna",
        "mammalians",
        "Herpetofauna",
        "districts",
        "subcatchment",
      ],
    },
    {
      label: "Wetland Water Quality",
      file: "wetland_wq",
      years: { 2025: "/geojson/wetland_water_quality_2025.geojson" },
      chartFields: [
        "Sub Catchment",
        "pH",
        "Temperature",
        "EC",
        "TDS",
        "Turbidity",
        "District",
      ],
    },
    {
      label: "Water resource inventory",
      file: "wetland_inventory",
      years: { 2025: "/geojson/water_resource_inventory_2025.geojson" },
      chartFields: [
        "SubCatchment",
        "pH",
        "Temperature",
        "Turbidity",
        "district",
      ],
    },
  ];

  const forestCategories = [
    {
      label: "Forest",
      file: "forest",
      years: { 2025: "geojson/forest_2025.geojson" },
      chartFields: [
        "collection_name",
        "district",
        "council",
        "sub_catchment",
        "village",
        "forest_type",
        "forest_management",
        "management_practice",
        "damage_type",
        "damage_cause",
      ],
    },
    {
      label: "Orchards",
      file: "orchards",
      years: { 2025: "/geojson/ochards_2025.geojson" },
      chartFields: [
        "fruit_tree_type",
        "source_of_seedling",
        "trees_age_years",
        "flowering_period",
        "ripening_period",
      ],
    },
  ];

  useEffect(() => {
    if (isDegraded) return; // keep degraded list intact
    if (!geoData?.features) {
      setSubcatchments([]);
      return;
    }
    const allSubs = geoData.features.map((f: any) => f.properties.SubCatch_C);
    setSubcatchments(Array.from(new Set(allSubs.filter(Boolean))));
  }, [geoData, isDegraded]);

  useEffect(() => {
    if (!isDegraded) return;
    // Use your master map of subcatchment -> file
    setSubcatchments(Object.keys(LandCoverGeoJSON));
  }, [isDegraded]);

  useEffect(() => {
    if (isDegraded && chartProperty) {
      // If your LandCoverGeoJSON keys are subcatchment codes/names, keep as-is:
      const subs = Object.keys(LandCoverGeoJSON);
      setSubcatchments(subs);
    }
  }, [isDegraded, chartProperty]);

  useEffect(() => {
    // Whenever the selected file changes, remove any existing raster
    if (rasterRef.current) {
      rasterRef.current.remove(); // remove from Leaflet
      rasterRef.current = null;
    }
  }, [selectedFile]);

  const generateColorMap = (data: typeof chartData) => {
    // Only proceed if data is categorical
    if (!Array.isArray(data) || data.length === 0 || !("name" in data[0])) {
      return {};
    }

    const categoricalData = data as { name: string; value: number }[];

    const top10 = [...categoricalData]
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const colors = [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
      "#bcbd22",
      "#17becf",
    ];

    const map: Record<string, string> = {};
    top10.forEach((item, i) => {
      map[item.name] = colors[i % colors.length];
    });

    return map;
  };

  

  const defaultPalettes: Record<string, string[]> = {
    pH: ["#d73027", "#fc8d59", "#91bfdb", "#4575b4"], // red → blue
    Temperature: ["#313695", "#74add1", "#fee090", "#f46d43", "#a50026"], // blue → red
    EC: ["#edf8fb", "#b2e2e2", "#66c2a4", "#238b45"], // light → green
    TDS: ["#f7fcf0", "#ccebc5", "#7bccc4", "#2b8cbe", "#08589e"], // green-blue
    Turbidity: ["#ffffcc", "#c2e699", "#78c679", "#31a354", "#006837"], // yellow → green
    litter: ["#fff7ec", "#fee8c8", "#fdd49e", "#fc8d59", "#d7301f"], // light brown → red
    herbaceousCover: ["#edf8fb", "#b2e2e2", "#66c2a4", "#238b45"],
    shrubCover: ["#f1eef6", "#bdc9e1", "#74a9cf", "#0570b0"],
    bareSoil: ["#ffffd4", "#fee391", "#fec44f", "#fe9929", "#d95f0e"], // yellow → brown
  };

  function generateBins(values: number[], numBins: number, palette: string[]) {
    if (!values.length) return [];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const step = (max - min) / numBins;

    return Array.from({ length: numBins }, (_, i) => ({
      min: min + i * step,
      max: i === numBins - 1 ? Infinity : min + (i + 1) * step,
      color: palette[i],
    }));
  }

  // Helper: pick the latest year key from a { [year]: url } object
  function getLatestYear(yearsObj: Record<string, string> | undefined) {
    if (!yearsObj) return "";
    const years = Object.keys(yearsObj).sort((a, b) => Number(b) - Number(a));
    return years[0] || "";
  }

  // helper near the top
  function makeHistogramBins(values: number[], bucketSize: number) {
    if (!values.length || !Number.isFinite(bucketSize) || bucketSize <= 0)
      return [];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const start = Math.floor(min / bucketSize) * bucketSize;
    const end = Math.ceil(max / bucketSize) * bucketSize;

    const bins: { label: string; value: number; center: number }[] = [];
    for (let b = start; b < end; b += bucketSize) {
      const label = `${b.toFixed(2)}–${(b + bucketSize).toFixed(2)}`;
      bins.push({ label, value: 0, center: b + bucketSize / 2 });
    }
    values.forEach((v) => {
      const idx = Math.min(
        bins.length - 1,
        Math.floor((v - start) / bucketSize)
      );
      if (idx >= 0 && idx < bins.length) bins[idx].value += 1;
    });
    return bins;
  }

  function useColorScale(chartProperty: string, geoData: any) {
    const key = chartProperty;
    const allValues = geoData.features
      .map((f: any) => f.properties?.[chartProperty])
      .filter((v: any) => typeof v === "number");

    if (!continuousColumns.includes(chartProperty)) {
      return () => "#999999"; // Categorical fallback
    }

    const palette = defaultPalettes[key] || ["#999999"];
    const bins = generateBins(allValues, palette.length, palette);

    return (value: number) => {
      const match = bins.find((b) => value >= b.min && value < b.max);
      return match ? match.color : "#999999";
    };
  }

  const getColorForFeature = (
    chartProperty: string,
    geoData: any,
    value: number | string
  ) => {
    if (
      continuousColumns.includes(chartProperty) &&
      typeof value === "number"
    ) {
      // Continuous → use discrete bins
      const getColor = useColorScale(chartProperty, geoData);
      return getColor(value);
    }

    // Categorical → fallback to existing colorMap
    return colorMap[value] || "#cccccc";
  };

  let currentLayer;

  if (selectedFile === "Wetland") {
    currentLayer = wetlandCategories.find((opt) => opt.file === wetlandSubType);
  } else if (selectedFile === "Forest") {
    currentLayer = forestCategories.find((opt) => opt.file === forestSubType); // Add this if you have forest subtypes
  } else if (selectedFile === "Landcover") {
    currentLayer = land_cover_categories.find(
      (opt: any) =>
        opt.value === LandCoverSubType || opt.file === LandCoverSubType
    ) as any; // Add this if you have forest subtypes
  } else {
    currentLayer = layerOptions.find((opt) => opt.file === selectedFile);
  }

  const continuousColumns = [
    "pH",
    "Temperature",
    "EC",
    "TDS",
    "Turbidity",
    "litter",
    "herbaceousCover",
    "shrubCover",
    "bareSoil",
    "food_consumption_score",
  ];

  const isContinuous = continuousColumns.includes(chartProperty);

  const allowedChartProperties = currentLayer?.chartFields || [];

  const isCategorical = (values: any[]): boolean => {
    const uniqueValues = Array.from(new Set(values));
    // If number of unique values is small and values are strings, likely categorical
    return (
      typeof uniqueValues[0] === "string" ||
      uniqueValues.length < Math.min(15, values.length * 0.5)
    );
  };

  // Improved style function for GeoJSON features
  const geoJsonStyle = (
    feature: GeoJSON.Feature<GeoJSON.Geometry, any> | undefined
  ): PathOptions => {
    if (!feature) {
      return {
        color: "gray",
        weight: 1,
      };
    }
    const config = layerOptions.find((ds) => ds.file === selectedFile);
    const value = feature?.properties?.[chartProperty];
    const baseColor = getColorForFeature(chartProperty, geoData, value); //colorMap[value] || "#cccccc";
    const isHighlighted = hoveredBarKey && value === hoveredBarKey;

    return {
      fillColor: baseColor,
      color: isHighlighted ? "#000000" : baseColor,
      weight: isHighlighted ? 3 : 1,
      opacity: 1,
      fillOpacity: isHighlighted ? 1 : 0.7,
    };
  };

  const pointToLayer = (feature: Feature<Geometry, any>, latlng: L.LatLng) => {
    return L.circleMarker(latlng, {
      radius: 5,
      fillColor: "#3388ff",
      color: "red",
      weight: 1,
      opacity: 1,
      fillOpacity: 0.7,
    });
  };

  const onEachFeature = (feature: Feature<Geometry, any>, layer: L.Layer) => {
    if (feature.properties) {
      const popupContent = Object.entries(feature.properties)
        .map(([key, value]) => {
          if (
            key === "species" &&
            typeof value === "object" &&
            value !== null
          ) {
            const speciesList = Object.entries(value)
              .map(([species, percentage]) => `${species}: ${percentage}`)
              .join("<br>");
            return `<strong>${key}:</strong><br>${speciesList}`;
          }

          return `<strong>${key}:</strong> ${value}`;
        })
        .join("<br>");

      layer.bindPopup(popupContent);
    }
  };

  // 1) Seed selection with your known IDs
  const HILITE_SUBS = [
    "SC3",
    "SC11",
    "CC65",
    "CC60",
    "CC23",
    "CC13",
    "CC6",
    "CC9",
    "SC47",
    "SC41",
    "SC35",
    "SC30",
    "SC28",
    "SC48",
    "SC17",
    "CC63",
    "SC52",
    "SC50",
  ];

  const HILITE_SET = new Set(HILITE_SUBS);

  // Reads the subcatchment id from feature properties (works across files)
  function getSub(p: any): string | null {
    return p?.SubCatch_C || p?.subcatchment_code || p?.SubCatchment || null;
  }
  const [allSubsData, setAllSubsData] = useState<FeatureCollection | null>(
    null
  );

  useEffect(() => {
    let fileToLoad = "";

    if (selectedFile === "Soil") {
      setGeoData(null);
      return; // handled by raster pipeline
    }

    // Degraded Landcover short-circuit (your existing guard)
    if (selectedFile === "Landcover" && isDegraded) {
      setGeoData(null);
      return; // degraded loader handles it elsewhere
    }

    // Try to find a config for the currently selected thing
    // 1) Landcover main/degraded is handled earlier — for main, pick category config
    // 2) Other layers come from layerOptions
    let base: any = null;
    if (selectedFile === "Landcover") {
      base = land_cover_categories.find(
        (c) =>
          c.value === LandCoverSubType || (c as any).file === LandCoverSubType
      );
    } else if (selectedFile === "Wetland") {
      base = wetlandCategories.find((c: any) => c.file === wetlandSubType);
    } else if (selectedFile === "Forest") {
      base = forestCategories.find((c: any) => c.file === forestSubType);
    } else {
      base = layerOptions.find(
        (l) => l.file === selectedFile || l.label === selectedFile
      );
    }

    const years = (base as any)?.years as Record<string, string> | undefined;
    if (years) {
      const yr =
        selectedYear ||
        Object.keys(years).sort((a, b) => Number(b) - Number(a))[0];
      fileToLoad = years?.[yr] || "";
    } else {
      // fallback to subtype URLs if any
      if (selectedFile === "Wetland") fileToLoad = wetlandSubType;
      else if (selectedFile === "Forest") fileToLoad = forestSubType;
      else fileToLoad = (base as any)?.file || "";
    }

    if (!fileToLoad) {
      setGeoData(null);
      return;
    }

    setGeoData(null);
    fetch(fileToLoad)
      .then((res) => res.json())
      .then((data) => {
        if (selectedFile === "Socio-economic" && data?.features) {
          const features = data.features.map((f: any) => {
            const p = f.properties || {};
            const fcs = computeFCS(p);
            return {
              ...f,
              properties: {
                ...p,
                food_consumption_score: Number.isFinite(fcs) ? fcs : null,
              },
            };
          });
          setGeoData({ ...data, features });
        } else {
          setGeoData(data);
        }
      })
      .catch((err) => {
        console.error("Error loading GeoJSON:", err);
        setGeoData(null);
      });
  }, [
    selectedFile,
    LandCoverSubType,
    wetlandSubType,
    forestSubType,
    selectedYear,
    isDegraded,
  ]);

  useEffect(() => {
    fetch("/subcatchments_.geojson")
      .then((res) => res.json())
      .then((data) => setAllSubsData(data))
      .catch((err) => console.error("Error loading subcatchments:", err));
  }, []);

  useEffect(() => {
    fetch("/landscapes_fixed.geojson") // <-- put your real path here
      .then((res) => res.json())
      .then((data) => setLandscapesData(data))
      .catch((err) => {
        console.error("Error loading landscapes:", err);
        setLandscapesData(null);
      });
  }, []);

  async function safeFetchJSON(url: string) {
    if (!url) throw new Error("No URL provided for GeoJSON.");
    const res = await fetch(encodeURI(url), { cache: "no-store" });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} for ${url}`);
    }
    const ct = res.headers.get("content-type") || "";
    const looksJSON =
      ct.includes("application/json") || ct.includes("geo+json");
    const text = await res.text();
    if (!looksJSON && text.trim().startsWith("<!DOCTYPE")) {
      throw new Error(
        `Received HTML instead of JSON from ${url} (likely 404 with SPA fallback).`
      );
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`Invalid JSON at ${url}: ${(e as Error).message}`);
    }
  }

  useEffect(() => {
    if (!geoData || !geoData.features) {
      setFeatureCount(0);
      setDistricts([]);
      return;
    }

    const features: Feature<Geometry, any>[] = geoData.features;

    // ✅ Count features
    setFeatureCount(features.length);

    // ✅ Get unique districts (adjust property name if needed!)
    const uniqueDistricts = Array.from(
      new Set(
        features
          .map(
            (f) =>
              f.properties?.district ||
              f.properties?.ADM_NAME ||
              f.properties?.NAME
          )

          .filter((d): d is string => typeof d === "string")
      )
    ) as string[];

    setDistricts(uniqueDistricts);
  }, [geoData]);

  useEffect(() => {
    if (geoData?.features && chartProperty) {
      const values = geoData.features
        .map((f: Feature<Geometry, any>) => f.properties?.[chartProperty])
        .filter((v: any) => v !== null && v !== undefined);
      setIsCat(isCategorical(values));
    }
  }, [geoData, chartProperty]);

  function getRasterValues(georaster: any): number[] {
    // assuming single-band raster
    const values: number[] = [];
    for (let y = 0; y < georaster.height; y++) {
      for (let x = 0; x < georaster.width; x++) {
        const val = georaster.values[0][y][x]; // band 0
        if (!isNaN(val)) values.push(val);
      }
    }
    return values;
  }

  const activeCategory = land_cover_categories.find(
    (c) => c.label === LandCoverSubType
  );

  useEffect(() => {
    async function loadDegraded() {
      if (!isDegraded || !selectedLULCSubcatchment) return;

      setGeoData(null); // 👈 clear old layer so old popups/features disappear
      const entry = LandCoverGeoJSON[selectedLULCSubcatchment];
      let filePath = "";

      if (typeof entry === "string") {
        // single URL case
        filePath = entry;
      } else if (entry && typeof entry === "object") {
        const yrs = entry as Record<string, string>;
        const yr =
          selectedYear ||
          Object.keys(yrs).sort((a, b) => Number(b) - Number(a))[0];
        filePath = (yr && yrs[yr]) || "";
      }

      try {
        if (!filePath) {
          throw new Error(
            `No file mapped for subcatchment "${selectedLULCSubcatchment}".`
          );
        }
        const data = await safeFetchJSON(filePath);
        setGeoData(data);
      } catch (err) {
        console.error("Error loading degraded subcatchment:", err);
        setGeoData(null);
        // Optional: show a toast or set some visible error state
      }
    }
    loadDegraded();
  }, [LandCoverSubType, selectedLULCSubcatchment, selectedYear]);

  useEffect(() => {
    if (geoData) {
      // Force map update after a short delay
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    }
  }, [geoData]);

  useEffect(() => {
    console.log(
      "Selected LULC Subcatchment changed:",
      selectedLULCSubcatchment
    );
  }, [selectedLULCSubcatchment]);

  useEffect(() => {
    if (!geoData?.features) return; // Exit if geoData is null or has no features
    console.log(
      "GeoData name:",
      geoData.features.map((f) => f.properties?.SubCatch_C)
    );
  }, [geoData]);

  //soil
  // Update raster when property changes
  useEffect(() => {
  if (!soilSubType) return;

  const entry =
    SoilRasters[soilSubType]?.[selectedSubcatchment] ??
    SoilRasters[soilSubType]?.["ALL"];

  if (!entry) {
    setSelectedRaster("");
    return;
  }

  if (typeof entry === "string") {
    // legacy single raster
    setSelectedRaster(entry);
  } else {
    // year map
    const years = Object.keys(entry).sort((a, b) => Number(b) - Number(a));
    const yr = (selectedYear && entry[selectedYear]) ? selectedYear : years[0];
    setSelectedYear(yr);
    setSelectedRaster(entry[yr]);
  }
}, [soilSubType, selectedSubcatchment, selectedYear]);

  // soil histogram
  function generateRanges(min: number, max: number, numClasses: number) {
    const step = (max - min) / numClasses;
    return Array.from({ length: numClasses }, (_, i) => ({
      min: min + i * step,
      max: i === numClasses - 1 ? max : min + (i + 1) * step,
      count: 0,
    }));
  }

  const filteredFeatures = useMemo(() => {
    if (!geoData?.features) return [];
    // For degraded landscapes, don't filter by subcatchment since each file is already specific
    if (isDegraded) {
      return geoData.features;
    }
    if (!selectedSubcatchment) return geoData.features;
    return geoData.features.filter(
      (f: any) => f.properties.SubCatch_C === selectedSubcatchment
    );
  }, [geoData, selectedSubcatchment, isDegraded]);

  const chartData = useMemo(() => {
    if (!filteredFeatures || filteredFeatures.length === 0) return [];

    // Only use area aggregation for degraded landscapes
    if (LandCoverSubType === "degraded landscapes") {
      const values = filteredFeatures
        .map((f) => ({
          category: f.properties?.[chartProperty],
          area: f.properties?.Area_km2, // Replace with your actual area column
        }))
        .filter((f) => f.category != null && f.area != null);

      const aggregation: Record<string, number> = {};
      values.forEach(({ category, area }) => {
        aggregation[category] = (aggregation[category] || 0) + area;
      });

      return Object.entries(aggregation).map(([name, totalArea]) => ({
        name,
        value: totalArea, // total area in km²
      }));
    }
    const values = filteredFeatures
      .map((f: Feature<Geometry, any>) => f?.properties?.[chartProperty])
      .filter((v): v is number | string => v !== null && v !== undefined);

    if (isContinuous) {
      // Process continuous data: convert to numbers and filter out non-numeric values
      const numericValues = values
        .map((v) => (typeof v === "number" ? v : parseFloat(v)))
        .filter((v) => !isNaN(v)); // Remove NaN values

      const minVal = Math.min(...numericValues);
      const maxVal = Math.max(...numericValues);

      console.log(`Using exact range: ${minVal} → ${maxVal}`);

      // Return with header row required by Google Charts
      return [
        ["Value"], // Header row must be a string
        ...numericValues.map((v) => [v]), // Each data point as an array
      ];
    }

    // Categorical logic (e.g. for bar chart)
    const aggregation: Record<string, number> = {};
    values.forEach((val) => {
      aggregation[val as string] = (aggregation[val as string] || 0) + 1;
    });

    return Object.entries(aggregation).map(([name, value]) => ({
      name,
      value,
    }));
  }, [
    filteredFeatures /*geoData*/,
    chartProperty,
    isContinuous,
    LandCoverSubType,
  ]);
  useEffect(() => {
    console.log("Chart property changed:", chartProperty);
    console.log("Is degraded:", isDegraded);
    console.log("LandCoverSubType:", LandCoverSubType);
  }, [chartProperty, isDegraded, LandCoverSubType]);

  const colorMap = useMemo(() => generateColorMap(chartData), [chartData]);

  // Color interpolation for continuous
  function interpolateColor(value: number, min: number, max: number): string {
    const ratio = (value - min) / (max - min);
    const r = Math.round((1 - ratio) * 247 + ratio * 8);
    const g = Math.round((1 - ratio) * 251 + ratio * 48);
    const b = Math.round((1 - ratio) * 255 + ratio * 107);
    return `rgb(${r},${g},${b})`;
  }

  const sharedFontStyle = {
    fontName: "Arial", // or your preferred font
    fontSize: 14,
    bold: false,
    italic: false,
    color: "#333",
  };
  const data = chartData as { name: string; value: number }[];

  function getHistogramOptions(
    chartProperty: string,
    minVal: number,
    maxVal: number
  ) {
    const range = maxVal - minVal;
    let bucketSize;

    if (chartProperty.toLowerCase().includes("pH")) {
      bucketSize = 0.5;
    } else if (chartProperty.toLowerCase().includes("Temperature")) {
      bucketSize = 2;
    } else if (chartProperty.toLowerCase().includes("herbaceousCover")) {
      bucketSize = 10;
    } else {
      bucketSize = range / 10; // default
    }

    return {
      bucketSize,
      //lastBucketPercentile: 95, // ignore extreme 5% for binning
    };
  }

  // 1️⃣ Calculate min and max from your actual data
  const numericValues =
    geoData?.features
      ?.map((f) => f.properties?.[chartProperty])
      .filter((v) => v !== null && v !== undefined)
      .map((v) => (typeof v === "number" ? v : parseFloat(v)))
      .filter((v) => !isNaN(v)) || [];

  const minVal = numericValues.length ? Math.min(...numericValues) : 0;
  const maxVal = numericValues.length ? Math.max(...numericValues) : 0;

  const { bucketSize } = getHistogramOptions(chartProperty, minVal, maxVal);
  function ZoomToSelection({ features }: { features: any[] }) {
    const map = useMap();

    useEffect(() => {
      if (features && features.length > 0) {
        const layer = L.geoJSON(features);
        map.fitBounds(layer.getBounds(), { padding: [20, 20] });
      }
    }, [features, map]);

    return null;
  }

  function ZoomToRaster({ bounds }: { bounds: L.LatLngBounds | null }) {
    const map = useMap();

    useEffect(() => {
      if (bounds) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }, [bounds, map]);

    return null;
  }

  function classifyByThresholds(property: string, values: number[]) {
    const thresholds = SoilThresholds[property];
    if (!thresholds) return [];

    const counts = thresholds.map((t) => ({ ...t, count: 0 }));

    values.forEach((val) => {
      const match = counts.find((t) => val >= t.min && val < t.max);
      if (match) match.count++;
    });

    return counts;
  }

  //pop up for degraded feature
  const onEachDegradedFeature = (
    feature: Feature<Geometry, any>,
    layer: L.Layer
  ) => {
    const p = feature.properties || {};
    const rows: Array<[string, any]> = [
      ["LCS3code", p.LCS3code],
      ["land_cover", p.land_cover],
      ["Area_km2", p.Area_km2],
      ["district", p.district],
      ["SubCatch_C", p.SubCatch_C],
    ];
    const html = rows
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `<strong>${k}:</strong> ${v}`)
      .join("<br>");
    layer.bindPopup(html || "No attributes");
  };

  return (
    <div className="app">
      <div className="banner">
        <img src="fao-logo-png-4.png" alt="AO Logo" className="banner-logo" />
        <h1 style={{ fontStyle: "italic" }}> ROLL field data dashboard </h1>
      </div>
      <div className="app-container">
        <div className="sidebar">
          <div className="sidebar-header">
            <h2>Roll Geo-Analytics Dashboard</h2>
            <p>
              {" "}
              This application serves as an interactive Dashboard to visualize
              baseline data for resilience indicators collected under ROLL
              project in the year 2025.
            </p>
          </div>
          <div className="sidebar-content">
            <h3>Project Information</h3>
            <p>
              The ROLL project aims to improve agricultural productivity and
              resilience in Lesotho through data-driven insights and geospatial
              analysis.
            </p>
          </div>
          <div className="layer-selector">
            <p>Select a dataset to display:</p>
            <select
              onChange={(e) => {
                const file = e.target.value;
                setSelectedFile(file);

                // reset cross-dataset state to avoid leaks from Landcover
                setLandCoverSubType("");
                setSelectedLULCSubcatchment("");
                setSelectedSubcatchment("");
                setChartProperty("");
                setWetlandSubType("");
                setForestSubType("");
                setGeoData(null);

                setLandCoverSubType("");
                // NEW: reset year when switching datasets
                setSelectedYear("");

                // Find the layer config
                const layer = layerOptions.find((opt) => opt.file === file);

                setTeams(layer ? layer.team : null);

                if (file === "Landcover") {
                  setLandCoverSubType("landcover"); // matches land_cover_categories[file/value]
                  const lc = land_cover_categories.find(
                    (c) => c.file === "landcover" || c.value === "landcover"
                  ) as any;
                  if (lc?.years)
                    setSelectedYear(
                      Object.keys(lc.years).sort(
                        (a, b) => Number(b) - Number(a)
                      )[0]
                    );
                  setChartProperty(lc?.chartFields?.[0] ?? "land_cover");
                }
              }}
              value={selectedFile}
            >
              <option value="">-- Choose a layer --</option>
              {layerOptions.map((layer) => (
                <option key={layer.file} value={layer.file}>
                  {layer.label}
                </option>
              ))}
            </select>

            {/* Landcover category picker */}
            {selectedFile === "Landcover" && (
              <div className="layer-selector">
                <p>Select Land Cover Category:</p>
                <select
                  value={LandCoverSubType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLandCoverSubType(val);
                    const cfg = land_cover_categories.find(
                      (c) => c.value === val || c.file === val
                    );
                    if (cfg && (cfg as any).years) {
                      const latest = getLatestYear((cfg as any).years);
                      setSelectedYear(latest);
                    } else {
                      setSelectedYear("");
                    }
                    if (cfg?.chartFields?.length) {
                      setChartProperty(cfg.chartFields[0]); // default a property so the dropdown has a value
                    } else {
                      setChartProperty(""); // no fields
                    }
                  }}
                >
                  <option value="">-- Choose a Land Cover Category --</option>
                  {land_cover_categories.map((opt) => (
                    <option
                      key={(opt as any).value || (opt as any).file}
                      value={(opt as any).value || (opt as any).file}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ✅ Generic YEAR picker for any layer that defines `years` */}
            {(() => {
              // figure out which config is currently active
              let base: any = null;
              if (selectedFile === "Landcover") {
                base = land_cover_categories.find((c) => c.value === LandCoverSubType || c.file === LandCoverSubType);
              } else if (selectedFile === "Wetland") {
                base = wetlandCategories.find((c) => c.file === wetlandSubType);
              } else if (selectedFile === "Forest") {
                base = forestCategories.find((c) => c.file === forestSubType);
              } else {
                base = layerOptions.find(
                  (l) => l.file === selectedFile || l.label === selectedFile
                );
              }

              const years = base?.years as Record<string, string> | undefined;
              if (!years) return null;

              const keys = Object.keys(years).sort(
                (a, b) => Number(b) - Number(a)
              );
              const value = keys.includes(selectedYear)
                ? selectedYear
                : keys[0];

              return (
                <div className="layer-selector">
                  <p>Select Year{isDegraded ? " (degraded)" : ""}:</p>
                  <select
                    value={value}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {keys.map((yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {isDegraded && (
              <>
                <div className="layer-selector">
                  <p>Select Property to Chart:</p>
                  <select
                    value={chartProperty}
                    onChange={(e) => {
                      const prop = e.target.value;

                      setChartProperty(prop);
                    }}
                  >
                    <option value="">-- Choose Property --</option>
                    {(currentLayer?.chartFields ?? []).map((field: string) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>

                {subcatchments.length > 0 && (
                  <div className="layer-selector">
                    <p>Select Subcatchment:</p>
                    <select
                      value={selectedLULCSubcatchment}
                      onChange={(e) => {
                        const sc = e.target.value;
                        setSelectedLULCSubcatchment(sc);

                        const entry = LandCoverGeoJSON[sc];
                        if (entry && typeof entry === "object") {
                          const years = Object.keys(
                            entry as Record<string, string>
                          );
                          const latest =
                            years.sort((a, b) => Number(b) - Number(a))[0] ||
                            "";
                          setSelectedYear(latest);
                        } else {
                          setSelectedYear(""); // single-URL case
                        }
                      }}
                    >
                      <option value="">-- Choose Subcatchment --</option>
                      {subcatchments.map((sc) => (
                        <option key={sc} value={sc}>
                          {sc}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Year picker */}

                {isDegraded &&
                  selectedLULCSubcatchment &&
                  (() => {
                    const entry = LandCoverGeoJSON[selectedLULCSubcatchment];
                    const years =
                      entry && typeof entry === "object"
                        ? Object.keys(entry as Record<string, string>)
                        : null;
                    if (!years || years.length === 0) return null;

                    return (
                      <div className="layer-selector">
                        <p>Select Year (degraded):</p>
                        <select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                        >
                          {years
                            .sort((a, b) => Number(b) - Number(a))
                            .map((yr) => (
                              <option key={yr} value={yr}>
                                {yr}
                              </option>
                            ))}
                        </select>
                      </div>
                    );
                  })()}

                {/* Layer info & districts display only after selectedChartField */}
                {!isDegraded && chartProperty && geoData && (
                  <>
                    <div className="layer-info">
                      <p>
                        <strong>Layer Information:</strong>
                      </p>
                      <p>Number of features: {featureCount}</p>
                    </div>

                    <div className="districts-list">
                      <p>
                        <strong>Districts covered:</strong>
                      </p>
                      <ul>
                        {districts.map((district, i) => (
                          <li key={i}>{district}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </>
            )}

            {selectedFile === "Wetland" && (
              <div className="layer-selector">
                <p>Select Wetland Category:</p>
                <select
                  value={wetlandSubType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setWetlandSubType(v);
                    const cfg = wetlandCategories.find((c) => c.file === v);
                    if (cfg?.years)
                      setSelectedYear(getLatestYear(cfg.years as any));
                    if (cfg?.chartFields?.length)
                      setChartProperty(cfg.chartFields[0]);
                  }}
                >
                  <option value="">-- Choose a Wetland Category --</option>
                  {wetlandCategories.map((opt) => (
                    <option key={opt.file} value={opt.file}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedFile === "Forest" && (
              <div className="layer-selector">
                <p>Select Forest Category:</p>
                <select
                  value={forestSubType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForestSubType(v);
                    const cfg = forestCategories.find((c) => c.file === v);
                    if (cfg?.years)
                      setSelectedYear(getLatestYear(cfg.years as any));
                    if (cfg?.chartFields?.length)
                      setChartProperty(cfg.chartFields[0]);
                  }}
                >
                  <option value="">-- Choose a Forest Category --</option>
                  {forestCategories.map((opt) => (
                    <option key={opt.file} value={opt.file}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedFile === "Soil" && (
  <div className="layer-selector">
    <p>Select Soil Property:</p>
    <select
      value={soilSubType}
      onChange={(e) => {
        setSoilSubType(e.target.value as CanonicalKey);
        // reset dependent state
        setSelectedSubcatchment("");
        setSelectedYear("");     // let effect pick latest if year-map exists
        setSelectedRaster("");
      }}
    >
      <option value="">-- Choose a Soil Property --</option>
      {SOIL_KEYS.map((k) => (
        <option key={k} value={k}>
          {k}
        </option>
      ))}
    </select>

    {/* Subcatchment selector (only shown if property selected) */}
    {soilSubType && SoilRasters[soilSubType] && (
      <>
        <p>Select Subcatchment:</p>
        <select
          value={selectedSubcatchment}
          onChange={(e) => {
            const sc = e.target.value;
            setSelectedSubcatchment(sc);
            // reset year so the effect below can set the latest year for this SC
            setSelectedYear("");
          }}
        >
          <option value="">-- Choose Subcatchment --</option>
          {/* If you want to allow a national fallback, uncomment next line */}
          {/* {SoilRasters[soilSubType]["ALL"] && <option value="ALL">ALL (National)</option>} */}
          {Object.keys(SoilRasters[soilSubType])
            .filter((s) => s !== "ALL")
            .map((sc) => (
              <option key={sc} value={sc}>
                {sc}
              </option>
            ))}
        </select>
      </>
    )}

    {/* Year selector: only appear if the selected entry is a year-map */}
    {soilSubType && (() => {
      const entry =
        SoilRasters[soilSubType]?.[selectedSubcatchment] ??
        SoilRasters[soilSubType]?.["ALL"];

      if (entry && typeof entry !== "string") {
        const years = Object.keys(entry).sort((a, b) => Number(b) - Number(a));
        const value = years.includes(selectedYear) ? selectedYear : years[0];
        return (
          <div className="layer-selector">
            <p>Select Year:</p>
            <select
              value={value}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
        );
      }
      return null;
    })()}
  </div>
)}

            
          </div>
          {geoData && (
            <>
              <div className="layer-info">
                <p>
                  <strong>Layer Information:</strong>
                </p>
                <p>Number of features: {featureCount}</p>
              </div>

              <div className="districts-list">
                <p>
                  <strong>Districts covered:</strong>
                </p>
                <ul>
                  {districts.map((district, i) => (
                    <li key={i}>{district}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {selectedTeams && (
            <div className="team-info">
              <p>
                <strong>🎲 Team:</strong> {selectedTeams}
              </p>
            </div>
          )}

          {!isDegraded && (currentLayer?.chartFields?.length ?? 0) > 0 && (
            <div className="layer-selector">
              <p>Select property to chart:</p>
              <select
                onChange={(e) => setChartProperty(e.target.value)}
                value={chartProperty}
              >
                <option value="">-- Choose a property --</option>

                {allowedChartProperties.map(
                  // initally was selecting all columns
                  (propName: string) => (
                    <option key={propName} value={propName}>
                      {propName}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {!isDegraded && geoData && subcatchments.length > 0 && (
            <div className="layer-selector">
              <p>Select Subcatchment:</p>
              <select
                value={selectedSubcatchment}
                onChange={(e) => setSelectedSubcatchment(e.target.value)}
              >
                <option value="">-- All Subcatchments --</option>
                {subcatchments.map((sub, i) => (
                  <option key={i} value={sub}>
                    {sub}
                  </option>
                ))}
              </select>
            </div>
          )}
          {chartData.length > 0 && (
            <div className="chart-container" style={{ width: "100%" }}>
              <div style={{ width: "100%", height: "400px" }}>
                {isContinuous ? (
                  (() => {
                    const numeric = filteredFeatures
                      .map((f: any) => f?.properties?.[chartProperty])
                      .filter((v: any) => v != null)
                      .map((v: any) =>
                        typeof v === "number" ? v : parseFloat(v)
                      )
                      .filter((v: number) => Number.isFinite(v)) as number[];

                    const { bucketSize } = getHistogramOptions(
                      chartProperty,
                      minVal,
                      maxVal
                    );
                    const bins = makeHistogramBins(numeric, bucketSize || 1);

                    return (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={bins}
                          margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                        >
                          <XAxis
                            dataKey="label"
                            angle={-45}
                            textAnchor="end"
                            interval={0}
                            height={80}
                            label={{
                              value: chartProperty,
                              position: "insideBottom",
                              offset: -50,
                            }}
                          />
                          <YAxis
                            label={{
                              value: "Frequency",
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <Tooltip />
                          <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                            {bins.map((b, i) => (
                              <Cell key={i} fill="#8884d8" />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()
                ) : (
                  <div style={{ width: "100%" }}>
                    <h3 style={{ textAlign: "center", marginBottom: "10px" }}>
                      {isDegraded
                        ? `Area distribution by ${chartProperty}`
                        : `Categories by ${chartProperty}`}
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={(chartData as { name: string; value: number }[])
                          .sort((a, b) => b.value - a.value)
                          .slice(0, 10)}
                        margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      >
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={80}
                          label={{
                            value: chartProperty,
                            position: "insideBottom",
                            offset: -50,
                            style: { sharedFontStyle },
                          }}
                          tickFormatter={(name) =>
                            name.length > 10
                              ? `${name.substring(0, 15)}…`
                              : name
                          }
                          tick={{
                            fontSize: sharedFontStyle.fontSize,
                            fill: sharedFontStyle.color,
                            fontFamily: sharedFontStyle.fontName,
                          }}
                        />

                        <YAxis
                          label={{
                            value: isDegraded ? "Area (km²)" : "Count",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: { sharedFontStyle },
                          }}
                          tick={{
                            fontSize: sharedFontStyle.fontSize,
                            fill: sharedFontStyle.color,
                            fontFamily: sharedFontStyle.fontName,
                          }}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          radius={[5, 5, 0, 0]}
                          onMouseOver={(_, index) => {
                            setHoveredBarKey(data[index].name);
                          }}
                          onMouseOut={() => {
                            setHoveredBarKey(null);
                          }}
                        >
                          {data.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={colorMap?.[entry.name] || "#8884d8"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedFile === "Soil" && soilStats && (
            <div className="soil-stats" style={{ marginTop: "20px" }}>
              <h3>Statistics for {soilSubType} </h3>
              <p>Min: {soilStats.min.toFixed(2)}</p>
              <p>Max: {soilStats.max.toFixed(2)}</p>
              <p>Mean: {soilStats.mean.toFixed(2)}</p>
              <p>Std Dev: {soilStats.std.toFixed(2)}</p>
            </div>
          )}

          {selectedFile === "Soil" &&
          histogramData &&
          histogramData.length > 0 ? (
            <div style={{ marginTop: "20px", width: "100%" }}>
              <h3>Histogram for {soilSubType}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={histogramData}>
                  <XAxis dataKey="label" angle={-45} interval={0} height={70} />
                  <YAxis
                    label={{
                      value: "Pixel Count",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip />
                  <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                    {histogramData.map((b, i) => (
                      <Cell key={i} fill={b.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            selectedFile === "Soil" && <p>No histogram data available.</p>
          )}
        </div>
        <div
          className="map-container"
          style={{ height: "100%", width: "100%" }}
        >
          <MapContainer
            center={[-29.5, 28.5]}
            zoom={8}
            //style={{ height: "100vh", width: "100vw" }}
            className="leaflet-map"
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles © Esri"
            />

            {landscapesData && showLandscapes && (
              <GeoJSON
                data={landscapesData}
                style={{
                  color: "#8b5cf6",
                  weight: 2,
                  dashArray: "4 2",
                  fillOpacity: 0.05,
                }}
              />
            )}

            {/* 1) Base outlines for ALL subcatchments */}
            {allSubsData && showSubcatchments && (
              <>
                <GeoJSON
                  data={allSubsData}
                  style={{ color: "#64748b", weight: 1, fillOpacity: 0 }}
                  interactive={false}
                  bubblingMouseEvents={false}
                />

                {/* 2) Highlight ONLY the chosen IDs */}
                {selectedSubcatchment ? (
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: allSubsData.features.filter(
                          (f: any) =>
                            (getSub(f.properties) || "") == selectedSubcatchment
                        ),
                      } as FeatureCollection
                    }
                    style={{ color: "#22c55e", weight: 2, fillOpacity: 0.08 }}
                    interactive={false}
                    bubblingMouseEvents={false}
                  />
                ) : (
                  /* (Optional) If you still want to highlight a predefined set when
         nothing is selected, keep your HILITE_SET fallback here */
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: allSubsData.features.filter((f: any) =>
                          HILITE_SET.has(getSub(f.properties) || "")
                        ),
                      } as FeatureCollection
                    }
                    style={{ color: "#22c55e", weight: 2, fillOpacity: 0.08 }}
                    interactive={false}
                    bubblingMouseEvents={false}
                  />
                )}
              </>
            )}

            {geoData && (
              <GeoJSON
                key={`${selectedSubcatchment || "all"}-${
                  selectedLULCSubcatchment || "all"
                }`} // force refresh on hover
                data={
                  {
                    type: "FeatureCollection",
                    features: filteredFeatures,
                  } as FeatureCollection
                }
                style={geoJsonStyle}
                pointToLayer={pointToLayer}
                onEachFeature={
                  isDegraded ? onEachDegradedFeature : onEachFeature
                }
              />
            )}

            {/* 🚀 Auto zoom whenever subcatchment selection changes */}
            <ZoomToSelection
              key={selectedSubcatchment || "all"}
              features={filteredFeatures}
            />

            {/* 👇 Add soil raster overlay */}
            {selectedFile === "Soil" && soilSubType && selectedRaster && (
              <>
                {" "}
                <RasterLayer
                  key={`${soilSubType}-${selectedSubcatchment}`}
                  file={selectedRaster}
                  property={soilSubType}
                  rasterRef={rasterRef}
                  legendRef={legendRef}
                  onRasterLoad={(georaster: GeoRaster) => {
                    // Skip if the same raster
                    if (rasterLoadedRef.current === georaster) return;
                    rasterLoadedRef.current = georaster;
                    // Fallback if bounds are missing
                    const bounds: [[number, number], [number, number]] = [
                      [georaster.ymin, georaster.xmin],
                      [georaster.ymax, georaster.xmax],
                    ];

                    setRasterBounds(L.latLngBounds(bounds));

                    const values = getRasterValues(georaster);
                    if (!values.length) return setSoilStats(null);

                    const min = Math.min(...values);
                    const max = Math.max(...values);
                    const mean =
                      values.reduce((a, b) => a + b, 0) / values.length;
                    const std = Math.sqrt(
                      values.reduce((a, b) => a + (b - mean) ** 2, 0) /
                        values.length
                    );

                    setSoilStats({ min, max, mean, std });

                    //setHistogramData(formattedRanges);
                    const histogramData = classifyByThresholds(
                      soilSubType,
                      values
                    );

                    setHistogramData(histogramData);
                  }}
                />
              </>
            )}
            {selectedRaster && rasterBounds && (
              <ZoomToRaster bounds={rasterBounds} />
            )}
          </MapContainer>
         
            
          <div className="right-panel">
            <h4>Reference overlays</h4>

            <label className="row">
              <input
                type="checkbox"
                checked={showSubcatchments}
                onChange={() => setShowSubcatchments((v) => !v)}
              />
              Subcatchments
            </label>

            <label className="row">
              <input
                type="checkbox"
                checked={showLandscapes}
                onChange={() => setShowLandscapes((v) => !v)}
              />
              Landscapes
            </label>

            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  if (!allSubsData) return;
                  const b = L.geoJSON(allSubsData as any).getBounds();
                  // fit using Leaflet's map instance
                  const map = (document as any)._leaflet_map || null;
                }}
                hidden
              />
            </div>
          </div>
          <div className="right-panel right-panel--download">
            <DataDownload
              geoData={geoData}
              filteredFeatures={filteredFeatures as any[]}
              chartData={chartData as any[]}
              histogramData={histogramData}
              isContinuous={isContinuous}
              isDegraded={isDegraded}
              selectedFile={selectedFile}
              selectedYear={selectedYear}
              LandCoverSubType={LandCoverSubType}
              wetlandSubType={wetlandSubType}
              forestSubType={forestSubType}
              selectedSubcatchment={selectedSubcatchment}
              selectedLULCSubcatchment={selectedLULCSubcatchment}
              chartProperty={chartProperty}
              countryIso="lso"
              // 🧪 soil raster-specific
              selectedRaster={selectedRaster}
              soilSubType={soilSubType || undefined}
              rasterRef={rasterRef}
              enableRasterPNG={false}
            />
          </div>
          {selectedFile === "Soil" && soilSubType && ranges ? (
            <Legend
              chartProperty={soilSubType}
              isContinuous={false}
              minVal={ranges[0]}
              maxVal={ranges[1]}
              gradientColors={["#2c7bb6", "#abd9e9", "#fdae61", "#d7191c"]}
              colorMap={Object.fromEntries(
                SoilThresholds[soilSubType].map((t) => [t.label, t.color])
              )}
            />
          ) : (
            <Legend
              chartProperty={chartProperty}
              isContinuous={isContinuous}
              minVal={minVal}
              maxVal={maxVal}
              colorMap={colorMap}
              gradientColors={
                selectedFile === "Soil"
                  ? ["#2c7bb6", "#abd9e9", "#fdae61", "#d7191c"] // soil gradient
                  : defaultPalettes[chartProperty] || ["#f7fbff", "#08306b"] // other continuous
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
